/**
 * Activity log queries and mutations.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.8
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  Activity,
  CreateActivityRequest,
  ListActivitiesQuery,
  UpdateActivityRequest,
} from '@nexus/contracts';

import * as activitiesApi from './activities.api';
import type { ActivityPage } from './activities.api';
import { queryKeys } from './query-keys';

export function useActivities(query: ListActivitiesQuery): UseQueryResult<ActivityPage> {
  return useQuery({
    queryKey: queryKeys.activities.list(query),
    queryFn: () => activitiesApi.listActivities(query),
  });
}

export function useCreateActivity(): UseMutationResult<
  Activity,
  Error,
  CreateActivityRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateActivityRequest) => activitiesApi.createActivity(body),
    onSuccess: () => {
      /* Every timeline is invalidated rather than only the one being viewed: an
         entry can be logged against a record open in another tab, and a
         timeline missing its newest entry is the kind of thing nobody
         reports. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
    },
  });
}

/** An edit: which entry, and the change to apply. */
export interface UpdateActivityVariables extends UpdateActivityRequest {
  id: string;
}

export function useUpdateActivity(): UseMutationResult<
  Activity,
  Error,
  UpdateActivityVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    /* The id travels in the variables, so one hook serves every entry in a
       timeline rather than one hook per row. */
    mutationFn: ({ id, ...body }: UpdateActivityVariables) =>
      activitiesApi.updateActivity(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
    },
  });
}

export function useDeleteActivity(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => activitiesApi.deleteActivity(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
    },
  });
}
